import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import differenceBy from 'lodash/differenceBy';
import { Button, Grid, FormControl, FormHelperText, MenuItem, Typography } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import { Organization, OrganizationRole } from 'types/schema-generated-types';
import Select from 'components/@common/Inputs/Select';
import { UserFormData } from '../types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inputLabel: {
      fontWeight: 600
    },
    organizationNameLabel: {
      overflow: 'hidden'
    },
    displayContainer: {},
    buttonContainer: {
      marginTop: theme.spacing(1)
    },
    selectInput: {
      marginTop: theme.spacing(1)
    }
  })
);

interface OrganizationRolesPickerProps {
  organizations?: Organization[];
  organizationRoles: OrganizationRole[];
  disabled?: boolean;
}

const OrganizationRolesPicker = (props: OrganizationRolesPickerProps) => {
  const { organizations, organizationRoles, disabled } = props;
  const { touched, values, errors, setFieldValue, handleChange, validateForm } = useFormikContext<
    UserFormData
  >();
  const { t } = useTranslation('OrganizationRolesPicker');
  const classes = useStyles();

  const errorOverrideMap: any = {
    // eslint-disable-next-line
    ['organizationRoles field must have at least 1 items']: t(
      'At least one Organization Role must be selected.'
    )
  };
  const handleAddClick = useCallback(() => {
    const { organization, role } = values;
    if (!organization || !role) return;

    const selectedRole = organizationRoles?.find(orgRole => String(orgRole.id) === String(role));
    const selectedOrganization = organizations?.find(
      org => String(org.id) === String(organization)
    );
    setFieldValue('organizationRoles', [
      { organization: selectedOrganization, role: selectedRole },
      ...values.organizationRoles
    ]);
    setFieldValue('organization', '');
    setFieldValue('role', '');
  }, [values, organizationRoles, organizations, setFieldValue]);

  const selectableRoles = useMemo(() => {
    const selectedRoles = values.organizationRoles
      ?.filter(orgRole => orgRole.organization.id === Number(values.organization))
      .map(orgRole => orgRole.role);
    return differenceBy(organizationRoles, selectedRoles, 'name');
  }, [organizationRoles, values]);

  const handleRemovalClick = (organizationId?: number, roleId?: number): void => {
    setFieldValue(
      'organizationRoles',
      values.organizationRoles.filter(
        orgRole => !(orgRole.organization.id === organizationId && orgRole.role.id === roleId)
      )
    );
  };
  const getErrorText = (error?: any) => {
    if (!errors?.organizationRoles) return '';
    if (errorOverrideMap?.[error]) return errorOverrideMap[error];
    return error ?? '';
  };
  // Required because for some reason setFieldValue's shouldValidate? parameter isn't applying
  // to this validation.
  useEffect(() => {
    if (!values.organization && !values.role && values.organizationRoles.length) {
      validateForm();
    }
  }, [values.organization, values.role, values.organizationRoles, validateForm]);

  return (
    <FormControl error={!!errors.organizationRoles} fullWidth={true}>
      <div>
        <Typography variant="body1" className={classes.inputLabel}>
          {t('Organization & Roles')}
        </Typography>
      </div>
      <FormHelperText>{getErrorText(errors?.organizationRoles)}</FormHelperText>
      <div className={classes.displayContainer}>
        {values.organizationRoles.map(
          ({ organization, role }: { organization: Organization; role: OrganizationRole }) => (
            <Grid
              container
              key={`${organization?.id}${role?.id}`}
              alignItems="center"
              justify="space-between"
              spacing={3}
            >
              <Grid item>
                <Typography>{organization?.name}</Typography>
                <Typography variant="subtitle2">{role?.displayName}</Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={(): void => handleRemovalClick(organization?.id, role?.id)}
                  size="small"
                  startIcon={<CancelIcon />}
                  disableElevation
                >
                  {t('Remove')}
                </Button>
              </Grid>
            </Grid>
          )
        )}
      </div>
      <Select
        className={classes.selectInput}
        disabled={disabled}
        error={Boolean(touched.organization && errors.organization)}
        helperText={touched.organization && errors.organization ? errors.organization : ''}
        label={t('Organization')}
        name="organization"
        onChange={handleChange}
        placeholder={t('Organization Name')}
        size="small"
        value={values.organization}
      >
        {organizations?.length ? (
          // eslint-disable-next-line react/display-name
          organizations?.map(org => (
            <MenuItem value={org.id} key={`organization${org.id}`}>
              {org.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No roles available</MenuItem>
        )}
      </Select>
      <Select
        className={classes.selectInput}
        disabled={disabled || !values.organization}
        error={Boolean(touched.role && errors.role)}
        helperText={touched.role && errors.role ? errors.role : ''}
        label={t('Role')}
        name="role"
        onChange={handleChange}
        placeholder={t('Role')}
        size="small"
        value={values.role}
      >
        {selectableRoles?.length ? (
          // eslint-disable-next-line react/display-name
          selectableRoles?.map(orgRole => (
            <MenuItem value={orgRole.id} key={`organizationRole${orgRole.id}`}>
              {orgRole.displayName}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>{t('No roles available')}</MenuItem>
        )}
      </Select>
      <Grid container justify="flex-end" className={classes.buttonContainer}>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={handleAddClick}
          disabled={disabled || !(values.organization && values.role)}
          disableElevation
        >
          {t('Add Role')}
        </Button>
      </Grid>
    </FormControl>
  );
};

export default OrganizationRolesPicker;
